import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  getEscrowOnboardingStep,
  setEscrowOnboardingStep,
  type IOnboardingStep,
} from 'src/utils/saasOnboardingLocalStorage';
import { EscrowPartyTypeEnum, AgreementFormEnum } from 'src/graphql/schema';
import { SaasEscrowOnboardingRoutesEnum } from 'src/pages/SaasEscrowOnboarding';

import { createRequiredContext } from '../createRequiredContext';

import { useSaasEscrow } from './SaasEscrowContext';

const [useSaasEscrowOnboarding, SaasEscrowOnboardingProvider] = createRequiredContext<{
  handleNextStep: () => void;
  handlePrevStep: () => void;
  totalSteps: number;
  currentStep: number;
  lastCompletedStep: number;
  isNextStepDisabled: boolean;
  showBackButton: boolean;
}>();

// Define the possible step sequences as constants for clarity
const CREATOR_DEPOSITOR_BIPARTITE_FULL_FLOW = [
  'members',
  'depositor',
  'beneficiary',
  'notification-settings',
  'activation-notifications',
  'storage-settings',
  'agreement-setup',
];
const CREATOR_TRIPARTITE_INITIAL_FLOW = {
  [EscrowPartyTypeEnum.Depositor]: [
    'members',
    'depositor',
    'beneficiary',
    'notification-settings',
    'asset-visibility',
    'storage-settings',
    'agreement-setup',
  ],
  [EscrowPartyTypeEnum.Beneficiary]: [
    'members',
    'depositor',
    'beneficiary',
    'asset-visibility',
    'storage-settings',
    'agreement-setup',
  ],
};

const createCreatorFlow = ({
  party,
  agreementForm,
  isThirdPartyAgreement,
}: {
  party: EscrowPartyTypeEnum;
  agreementForm: AgreementFormEnum;
  isThirdPartyAgreement: boolean;
}): IOnboardingStep[] => {
  const baseFlow =
    agreementForm === AgreementFormEnum.Bipartite
      ? CREATOR_DEPOSITOR_BIPARTITE_FULL_FLOW
      : CREATOR_TRIPARTITE_INITIAL_FLOW[party];
  const flowSteps = [...baseFlow, ...(isThirdPartyAgreement ? [] : ['agreement-details']), 'summary'];

  return flowSteps as IOnboardingStep[];
};

const createInvitedFlow = (party: EscrowPartyTypeEnum): IOnboardingStep[] => [
  'members',
  party,
  'notification-settings',
  'summary',
];

function createSteps({
  party,
  agreementForm,
  isInvitedParty,
  isThirdPartyAgreement,
}: {
  party: EscrowPartyTypeEnum;
  agreementForm: AgreementFormEnum;
  isInvitedParty: boolean;
  isThirdPartyAgreement: boolean;
}): IOnboardingStep[] {
  if (isInvitedParty) return createInvitedFlow(party);

  return createCreatorFlow({ party, agreementForm, isThirdPartyAgreement });
}

// Define a linked list node structure
type IOnboardingStepNode = {
  step: IOnboardingStep;
  prev: IOnboardingStepNode | null;
  next: IOnboardingStepNode | null;
};

function buildLinkedList({
  party,
  agreementForm,
  isInvitedParty,
  isThirdPartyAgreement,
}: {
  party: EscrowPartyTypeEnum;
  agreementForm: AgreementFormEnum;
  isInvitedParty: boolean;
  isThirdPartyAgreement: boolean;
}): IOnboardingStepNode[] {
  const steps = createSteps({ party, agreementForm, isInvitedParty, isThirdPartyAgreement });
  const nodes: IOnboardingStepNode[] = [];

  steps.forEach((step, i) => {
    const node = {
      step,
      prev: i > 0 ? nodes[i - 1] : null,
      next: null,
    };

    if (i > 0) {
      nodes[i - 1].next = node;
    }

    nodes.push(node);
  });

  return nodes;
}

const SaasEscrowOnboardingContextProvider = ({ children }: React.PropsWithChildren) => {
  const {
    saasEscrow: {
      id,
      currentCompanyRole,
      startedBy,
      agreement,
      depositorConfigured,
      beneficiaryConfigured,
      agreementSettingsConfigured,
    },
  } = useSaasEscrow();
  const navigate = useNavigate();

  const isInvitedParty = currentCompanyRole !== startedBy;
  const isThirdPartyAgreement = Boolean(agreement?.thirdPartyAgreement);
  const stepLinkedList = buildLinkedList({
    party: currentCompanyRole!,
    agreementForm: agreement!.agreementForm!,
    isInvitedParty,
    isThirdPartyAgreement,
  });

  // Create a map for easy lookup by step name
  const stepLinkedListMap = stepLinkedList.reduce(
    (map, node) => {
      map[node.step] = node;
      return map;
    },
    {} as Record<IOnboardingStep, IOnboardingStepNode>,
  );

  const { '*': slug } = useParams<{ '*': SaasEscrowOnboardingRoutesEnum }>();

  const lastCompletedStep = getEscrowOnboardingStep(id) || SaasEscrowOnboardingRoutesEnum.Members;
  const currentStep = slug && slug in stepLinkedListMap ? slug : SaasEscrowOnboardingRoutesEnum.Members;
  const currentStepIndex = stepLinkedList.findIndex((step) => step.step === currentStep) + 1;
  const lastCompletedStepIndex = stepLinkedList.findIndex((step) => step.step === lastCompletedStep) + 1;

  const handleNextStep = useCallback(() => {
    const currentNode = stepLinkedListMap[currentStep];

    if (!currentNode) return;

    const nextNode = currentNode.next;

    // Already at last step
    if (!nextNode) return;

    // Store the next step in the local storage
    if (currentStepIndex >= lastCompletedStepIndex) setEscrowOnboardingStep(id, nextNode.step);

    // Build URL for next step
    const url = `/saas-escrows/onboarding/${id}/${nextNode.step}`;

    navigate(url);
  }, [currentStep, id, stepLinkedListMap, navigate, lastCompletedStepIndex, currentStepIndex]);

  const handlePrevStep = useCallback(() => {
    const currentNode = stepLinkedListMap[currentStep];

    if (!currentNode) return;

    const prevNode = currentNode.prev;

    // Already at first step
    if (!prevNode) return;

    // Build URL for previous step
    const url = `/saas-escrows/onboarding/${id}/${prevNode.step}`;

    navigate(url);
  }, [currentStep, id, stepLinkedListMap, navigate]);

  const nextStepDisabledStates = {
    [SaasEscrowOnboardingRoutesEnum.Depositor]: !depositorConfigured,
    [SaasEscrowOnboardingRoutesEnum.Beneficiary]: !beneficiaryConfigured,
    [SaasEscrowOnboardingRoutesEnum.AgreementDetails]: !agreementSettingsConfigured,
  };
  const isNextStepDisabled =
    currentStep in nextStepDisabledStates
      ? nextStepDisabledStates[currentStep as keyof typeof nextStepDisabledStates]
      : false;

  const providerValue = {
    handleNextStep,
    handlePrevStep,
    isNextStepDisabled,
    // For non-invited parties (creators), we add an extra step and adjust indices accordingly
    // For invited parties, we use the base step count and indices
    ...calculateStepMetrics({
      isInvitedParty,
      baseStepCount: stepLinkedList.length,
      currentIndex: currentStepIndex,
      lastCompletedIndex: lastCompletedStepIndex,
    }),
  };

  return <SaasEscrowOnboardingProvider value={providerValue}>{children}</SaasEscrowOnboardingProvider>;
};

function calculateStepMetrics({
  isInvitedParty,
  baseStepCount,
  currentIndex,
  lastCompletedIndex,
}: {
  isInvitedParty: boolean;
  baseStepCount: number;
  currentIndex: number;
  lastCompletedIndex: number;
}) {
  if (isInvitedParty)
    return {
      totalSteps: baseStepCount,
      currentStep: currentIndex,
      lastCompletedStep: lastCompletedIndex - 1,
      showBackButton: currentIndex > 1,
    };

  return {
    totalSteps: baseStepCount + 1,
    currentStep: currentIndex + 1,
    lastCompletedStep: lastCompletedIndex,
    showBackButton: currentIndex > 1,
  };
}

export { useSaasEscrowOnboarding, SaasEscrowOnboardingContextProvider };
